import { useCallback, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface PushOptions {
  displayIds?: string[];
  playlistId?: string;
  contentItemId?: string;
}

export function useContentPush(businessId: string) {
  const [pushing, setPushing] = useState(false);

  const pushRefresh = useCallback(async (displayIds?: string[]) => {
    setPushing(true);
    try {
      const { data, error } = await supabase.functions.invoke('signage-push-content', {
        body: {
          business_id: businessId,
          display_ids: displayIds,
          action: 'refresh',
        },
      });

      if (error) throw error;

      if (data?.displays_notified > 0) {
        toast.success(`Refreshed ${data.displays_notified} display(s)`);
      } else {
        toast.info('No active displays to refresh');
      }

      return data;
    } catch (err) {
      console.error('Failed to push refresh:', err);
      toast.error('Failed to refresh displays');
      return null;
    } finally {
      setPushing(false);
    }
  }, [businessId]);

  const pushReload = useCallback(async (displayIds?: string[]) => {
    setPushing(true);
    try {
      const { data, error } = await supabase.functions.invoke('signage-push-content', {
        body: {
          business_id: businessId,
          display_ids: displayIds,
          action: 'reload',
        },
      });

      if (error) throw error;

      if (data?.displays_notified > 0) {
        toast.success(`Reloaded ${data.displays_notified} display(s)`);
      } else {
        toast.info('No active displays to reload');
      }

      return data;
    } catch (err) {
      console.error('Failed to push reload:', err);
      toast.error('Failed to reload displays');
      return null;
    } finally {
      setPushing(false);
    }
  }, [businessId]);

  const pushContent = useCallback(async (options: PushOptions) => {
    setPushing(true);
    try {
      const { data, error } = await supabase.functions.invoke('signage-push-content', {
        body: {
          business_id: businessId,
          display_ids: options.displayIds,
          action: 'update_content',
          payload: {
            playlist_id: options.playlistId,
            content_item_id: options.contentItemId,
          },
        },
      });

      if (error) throw error;

      if (data?.displays_notified > 0) {
        toast.success(`Updated ${data.displays_notified} display(s)`);
      } else {
        toast.info('No active displays to update');
      }

      return data;
    } catch (err) {
      console.error('Failed to push content:', err);
      toast.error('Failed to update displays');
      return null;
    } finally {
      setPushing(false);
    }
  }, [businessId]);

  return {
    pushing,
    pushRefresh,
    pushReload,
    pushContent,
  };
}
